import { clamp01, dist, len, smoothstep, sub, v3, type Vec3 } from '../core/math';
import { Ring } from '../core/ring';
import { leadSide, type Side } from '../pose/landmarks';
import type { PoseFrame } from '../pose/types';
import { punchNumber, type PunchEvent, type PunchType } from './events';

/**
 * Per-arm punch recogniser.
 *
 * WHY A HAND-BUILT FSM RATHER THAN A LEARNED CLASSIFIER
 * -----------------------------------------------------
 * A sequence classifier (LSTM/TCN/transformer over landmark windows) needs a
 * labelled corpus that does not exist, must run every frame on top of BlazePose,
 * and — the killer — produces a *label* with no onset time. A boxing game needs
 * the onset (to detect counters), the peak (to resolve the hit), and the recovery
 * (to punish sloppy hands). An FSM over physically meaningful quantities gives all
 * three for free, is debuggable frame-by-frame, and costs microseconds.
 *
 * The honest cost: hand-tuned thresholds generalise worse across body types and
 * camera placements than a well-trained model would. Calibration is what buys
 * that back — every threshold below is expressed in shoulder widths and in
 * fractions of *this user's* arm length, not in pixels.
 *
 *   IDLE ──ė>ON & speed>MIN──▶ EXTEND ──ė≈0 or e>0.94──▶ PEAK
 *     ▲                          │ (travel too small)      │
 *     └──────────────────────────┘                         │ ė<-OFF
 *     ▲                                                    ▼
 *     └────── e back near start, ≥MIN_CYCLE ms ────────  RETRACT
 */

type State = 'idle' | 'extend' | 'peak' | 'retract';

const TUNING = {
  /** Extension rate (per second) that opens a punch. */
  rateOn: 1.5,
  /** Extension rate below which we call the peak. */
  rateStall: 0.35,
  /** Extension rate (negative) that confirms retraction. */
  rateOff: -0.9,
  /** Minimum fist speed at onset, sw/s. Filters out slow repositioning. */
  minOnsetSpeed: 1.8,
  /** Minimum extension gained from start to peak. Filters twitches. */
  minTravel: 0.16,
  /** Minimum peak fist speed for the punch to count at all, sw/s. */
  minPeakSpeed: 2.6,
  /** Full extension short-circuit. */
  fullExtension: 0.94,
  /** Refractory period per arm, ms. Faster than this is not physically a new punch. */
  refractoryMs: 110,
  /** Anything longer than this is a push, not a punch. */
  maxExtendMs: 600,
  /** Axis-dominance ratio needed to call a straight punch. */
  straightRatio: 0.6,
};

interface Snapshot {
  t: number;
  wrist: Vec3;
  extension: number;
  yaw: number;
  speed: number;
}

let nextId = 1;

export class ArmPunchFSM {
  private state: State = 'idle';
  private start: Snapshot | null = null;
  private peak: Snapshot | null = null;
  private peakExtension = 0;
  private maxSpeed = 0;
  private lastEmit = -1e9;
  private history = new Ring<Snapshot>(40);
  /** Lowest extension seen in the ~250 ms before onset — the cocking depth. */
  private telegraph = 0;
  private pendingPeak: { punch: PunchEvent; snapshot: Snapshot } | null = null;

  constructor(readonly side: Side) {}

  reset(): void {
    this.state = 'idle';
    this.start = null;
    this.peak = null;
    this.pendingPeak = null;
    this.history.clear();
  }

  get isThrowing(): boolean {
    return this.state === 'extend' || this.state === 'peak';
  }

  /**
   * Advances the FSM. Returns a punch at the *peak* — i.e. as soon as the fist
   * stops travelling forward — not when the hand returns. Waiting for the return
   * would add 150-250 ms of latency to hit feedback, which reads as broken.
   * `recoveryMs` is therefore patched in later via `onRecovered`.
   */
  update(f: PoseFrame): { peak?: PunchEvent; onset?: boolean; recovered?: PunchEvent } {
    const a = f.arms[this.side];
    const snap: Snapshot = { t: f.t, wrist: { ...a.wrist }, extension: a.extension, yaw: f.frame.yaw, speed: a.speed };
    this.history.push(snap);

    const out: { peak?: PunchEvent; onset?: boolean; recovered?: PunchEvent } = {};

    switch (this.state) {
      case 'idle': {
        if (
          a.extensionRate > TUNING.rateOn &&
          a.speed > TUNING.minOnsetSpeed &&
          f.t - this.lastEmit > TUNING.refractoryMs
        ) {
          this.state = 'extend';
          // Back-date the onset to where the extension actually began: the FSM
          // trips a frame or two late, and counter-punch timing is measured in
          // tens of milliseconds.
          this.start = this.backdatedOnset(snap);
          this.telegraph = this.cockingDepth(this.start.extension);
          this.peakExtension = a.extension;
          // Must be seeded here. Leaving it holding the *previous* punch's peak
          // meant a punch whose first extend-frame did not beat the seed value
          // was classified from a displacement vector spanning two punches.
          this.peak = snap;
          this.maxSpeed = a.speed;
          out.onset = true;
        }
        break;
      }
      case 'extend': {
        this.maxSpeed = Math.max(this.maxSpeed, a.speed);
        if (a.extension > this.peakExtension) {
          this.peakExtension = a.extension;
          this.peak = snap;
        }
        const elapsed = f.t - (this.start?.t ?? f.t);
        const stalled = a.extensionRate < TUNING.rateStall;
        if (stalled || a.extension >= TUNING.fullExtension || elapsed > TUNING.maxExtendMs) {
          const p = this.tryEmit(f);
          if (p) {
            out.peak = p;
            this.pendingPeak = { punch: p, snapshot: this.peak ?? snap };
            this.state = 'peak';
          } else {
            this.state = 'idle';
            this.start = null;
          }
        }
        break;
      }
      case 'peak': {
        if (a.extensionRate < TUNING.rateOff) this.state = 'retract';
        else if (f.t - (this.peak?.t ?? f.t) > 400) this.state = 'retract';
        break;
      }
      case 'retract': {
        const backHome = a.extension <= (this.start?.extension ?? 0.5) + 0.09 || a.guard > 0.7;
        if (backHome) {
          if (this.pendingPeak) {
            this.pendingPeak.punch.recoveryMs = f.t - this.pendingPeak.punch.tPeak;
            this.pendingPeak.punch.tEnd = f.t;
            out.recovered = this.pendingPeak.punch;
            this.pendingPeak = null;
          }
          this.state = 'idle';
          this.start = null;
        }
        break;
      }
    }
    return out;
  }

  /** Walk back through history to the frame where extension started rising. */
  private backdatedOnset(current: Snapshot): Snapshot {
    let best = current;
    for (let i = 1; i < this.history.size && i < 8; i++) {
      const s = this.history.at(i);
      if (!s) break;
      if (s.extension <= best.extension) best = s;
      else break;
    }
    return best;
  }

  /** How much the hand retracted in the 300 ms before firing. */
  private cockingDepth(onsetExtension: number): number {
    let maxE = onsetExtension;
    for (let i = 0; i < this.history.size; i++) {
      const s = this.history.at(i);
      if (!s) break;
      if (s.t < (this.history.last?.t ?? 0) - 300) break;
      maxE = Math.max(maxE, s.extension);
    }
    return Math.max(0, maxE - onsetExtension);
  }

  private tryEmit(f: PoseFrame): PunchEvent | null {
    const start = this.start;
    const peak = this.peak ?? this.history.last!;
    if (!start) return null;

    const travel = this.peakExtension - start.extension;
    if (travel < TUNING.minTravel) return null;
    if (this.maxSpeed < TUNING.minPeakSpeed) return null;

    const d = sub(peak.wrist, start.wrist);
    const mag = Math.max(1e-4, len(d));
    const isLead = this.side === leadSide(f.stance);
    const shoulderY = f.arms[this.side].shoulder.y;

    const { type, confidence: shape } = classify(d, mag, isLead, f.arms[this.side].elbowAngle);

    // Pose quality gates confidence hard: a punch inferred from a half-tracked
    // arm should influence the match less than a cleanly seen one, rather than
    // being silently accepted or silently dropped.
    const qualityTerm = smoothstep(0.55, 0.9, f.quality);
    const travelTerm = smoothstep(TUNING.minTravel, 0.35, travel);
    const speedTerm = smoothstep(TUNING.minPeakSpeed, 7, this.maxSpeed);
    const confidence = clamp01(shape * (0.45 + 0.25 * qualityTerm + 0.15 * travelTerm + 0.15 * speedTerm));

    const height = peak.wrist.y - shoulderY;
    const target = height > -0.28 ? 'head' : 'body';

    this.lastEmit = f.t;

    return {
      id: nextId++,
      side: this.side,
      isLead,
      type,
      number: punchNumber(type, isLead),
      target,
      tStart: start.t,
      tPeak: peak.t,
      tEnd: peak.t,
      extension: this.peakExtension,
      speed: this.maxSpeed,
      reach: peak.wrist.z,
      lateral: peak.wrist.x,
      height,
      hipDrive: Math.abs(peak.yaw - start.yaw),
      telegraph: this.telegraph,
      recoveryMs: 0,
      confidence,
    };
  }
}

/**
 * Punch taxonomy from the fist's displacement vector in torso space.
 *
 * This is where the "infer from body states" thesis pays off. Rather than asking
 * "which of six classes is this?", we ask "which axis did the fist travel along,
 * relative to the chest?" — a question with an unambiguous geometric answer that
 * does not care about lighting, clothing, or whether the model has seen boxing.
 *
 *   forward-dominant  -> straight (jab if lead, cross if rear)
 *   lateral-dominant  -> hook
 *   upward-dominant   -> uppercut
 *   forward + downward arc from the rear hand -> overhand
 */
function classify(d: Vec3, mag: number, isLead: boolean, elbowAngle: number): { type: PunchType; confidence: number } {
  const fz = d.z / mag; // forward
  const fx = Math.abs(d.x) / mag; // lateral
  const fy = d.y / mag; // up positive

  const straight = Math.max(0, fz);
  const hook = fx;
  const upper = Math.max(0, fy);

  // A near-locked elbow strongly implies a straight; a folded elbow implies a
  // hook or uppercut. This is an independent signal from the trajectory, so it
  // meaningfully sharpens the decision rather than just echoing it.
  const elbowStraightness = smoothstep(120, 165, elbowAngle);
  const scores: Array<[PunchType, number]> = [
    [isLead ? 'jab' : 'cross', straight * (0.65 + 0.35 * elbowStraightness)],
    ['hook', hook * (0.65 + 0.35 * (1 - elbowStraightness))],
    ['uppercut', upper * (0.6 + 0.4 * (1 - elbowStraightness))],
  ];
  scores.sort((a, b) => b[1] - a[1]);
  let [type, top] = scores[0];
  const second = scores[1][1];

  // Overhand: a rear straight that arcs steeply downward onto the target.
  // The threshold has to be well past -0.3: an ordinary cross already travels
  // downward, because the hand starts at your own cheek and finishes at their
  // chin. At -0.3 every rear straight to the head was called an overhand.
  if (!isLead && type === 'cross' && fy < -0.5 && fz > TUNING.straightRatio) type = 'overhand';

  // Confidence is the *margin*, not the winning score. A 0.51/0.49 split should
  // not read as confident just because the winner scored high in absolute terms.
  const margin = clamp01((top - second) / Math.max(0.15, top));
  return { type, confidence: clamp01(0.35 + 0.65 * margin) };
}

/** Both arms, with cross-arm arbitration. */
export class PunchRecogniser {
  private fsms: Record<Side, ArmPunchFSM> = {
    left: new ArmPunchFSM('left'),
    right: new ArmPunchFSM('right'),
  };

  reset(): void {
    this.fsms.left.reset();
    this.fsms.right.reset();
  }

  get busyHands(): number {
    return (this.fsms.left.isThrowing ? 1 : 0) + (this.fsms.right.isThrowing ? 1 : 0);
  }

  update(f: PoseFrame): {
    peaks: PunchEvent[];
    onsets: Side[];
    recovered: PunchEvent[];
  } {
    const peaks: PunchEvent[] = [];
    const onsets: Side[] = [];
    const recovered: PunchEvent[] = [];
    for (const side of ['left', 'right'] as Side[]) {
      const r = this.fsms[side].update(f);
      if (r.onset) onsets.push(side);
      if (r.peak) peaks.push(r.peak);
      if (r.recovered) recovered.push(r.recovered);
    }
    return { peaks, onsets, recovered };
  }
}

/** Exposed for the tuning overlay. */
export const PUNCH_TUNING = TUNING;
export const _internals = { classify, dist, v3, clamp01 };
